import client from './client';

export type SortDirection = 'asc' | 'desc';
export type OrgSortKey = 'name' | 'billingStatus' | 'planType' | 'monthlyCreditAllowance' | 'memberCount' | 'creditBalance' | 'createdAt';
export type UserSortKey = 'email' | 'displayName' | 'orgRole' | 'organizationName' | 'expiryDate' | 'lastLoginAt' | 'createdAt';
export type TransactionSortKey = 'createdAt' | 'type' | 'amount' | 'balanceAfter' | 'organizationId' | 'description' | 'organizationName' | 'userEmail';

const BACKOFFICE_TOKEN_KEY = 'backoffice_token';
const BACKOFFICE_EXPIRY_KEY = 'backoffice_expiry';

export function getBackofficeToken(): string | null {
  const expiry = sessionStorage.getItem(BACKOFFICE_EXPIRY_KEY);
  if (expiry && Date.now() > Number.parseInt(expiry, 10)) {
    clearBackofficeToken();
    return null;
  }
  return sessionStorage.getItem(BACKOFFICE_TOKEN_KEY);
}

export function setBackofficeToken(token: string, expiresInSeconds: number): void {
  sessionStorage.setItem(BACKOFFICE_TOKEN_KEY, token);
  sessionStorage.setItem(
    BACKOFFICE_EXPIRY_KEY,
    String(Date.now() + expiresInSeconds * 1000)
  );
}

export function clearBackofficeToken(): void {
  sessionStorage.removeItem(BACKOFFICE_TOKEN_KEY);
  sessionStorage.removeItem(BACKOFFICE_EXPIRY_KEY);
}

function backofficeHeaders() {
  const token = getBackofficeToken();
  return token ? { 'X-Backoffice-Token': token } : {};
}

// Auth
export async function verifyBackofficePassword(
  password: string
): Promise<{ token: string; expiresInSeconds: number }> {
  const { data } = await client.post('/api/v1/backoffice/auth/verify-password', { password });
  return data;
}

// Organizations
export async function fetchOrganizations(params: {
  page?: number;
  size?: number;
  search?: string;
  sort?: OrgSortKey;
  direction?: SortDirection;
}) {
  const { data } = await client.get('/api/v1/backoffice/organizations', {
    params,
    headers: backofficeHeaders(),
  });
  return data;
}

export interface CreatedOrganization {
  readonly orgId: string;
  readonly name: string;
  readonly superadminEmail: string;
  /** Returned once and never retrievable again. */
  readonly superadminPassword: string;
  readonly camEnabled: boolean;
}

export async function createOrganization(
  name: string,
  enableCamAccess: boolean
): Promise<CreatedOrganization> {
  const { data } = await client.post(
    '/api/v1/backoffice/organizations',
    { name, enableCamAccess },
    { headers: backofficeHeaders() }
  );
  return data;
}

export async function fetchOrganization(orgId: string) {
  const { data } = await client.get(`/api/v1/backoffice/organizations/${orgId}`, {
    headers: backofficeHeaders(),
  });
  return data;
}

export async function updateOrganization(
  orgId: string,
  updates: Record<string, unknown>
) {
  const { data } = await client.put(`/api/v1/backoffice/organizations/${orgId}`, updates, {
    headers: backofficeHeaders(),
  });
  return data;
}

// Users
export async function fetchUsers(params: {
  page?: number;
  size?: number;
  search?: string;
  sort?: UserSortKey;
  direction?: SortDirection;
}) {
  const { data } = await client.get('/api/v1/backoffice/users', {
    params,
    headers: backofficeHeaders(),
  });
  return data;
}

export async function fetchUser(userId: string) {
  const { data } = await client.get(`/api/v1/backoffice/users/${userId}`, {
    headers: backofficeHeaders(),
  });
  return data;
}

export async function updateUserRole(userId: string, role: string) {
  const { data } = await client.put(
    `/api/v1/backoffice/users/${userId}/role`,
    { role },
    { headers: backofficeHeaders() }
  );
  return data;
}

export async function updateUserExpiry(userId: string, expiryDate: string) {
  const { data } = await client.put(
    `/api/v1/backoffice/users/${userId}/expiry`,
    { expiryDate },
    { headers: backofficeHeaders() }
  );
  return data;
}

export async function banUser(userId: string, reason?: string) {
  const { data } = await client.post(
    `/api/v1/backoffice/users/${userId}/ban`,
    { reason: reason || null },
    { headers: backofficeHeaders() }
  );
  return data;
}

export async function unbanUser(userId: string) {
  const { data } = await client.post(
    `/api/v1/backoffice/users/${userId}/unban`,
    {},
    { headers: backofficeHeaders() }
  );
  return data;
}

// Billing
export async function fetchTransactions(params: {
  search?: string;
  page?: number;
  size?: number;
  sort?: TransactionSortKey;
  direction?: SortDirection;
}) {
  const { data } = await client.get('/api/v1/backoffice/billing/transactions', {
    params,
    headers: backofficeHeaders(),
  });
  return data;
}

export async function grantCredits(request: {
  organizationId: string;
  amount: number;
  expiryMonths: number;
}) {
  const { data } = await client.post('/api/v1/backoffice/billing/grant-credits', request, {
    headers: backofficeHeaders(),
  });
  return data;
}

export async function debitCredits(request: {
  organizationId: string;
  amount: number;
  reason?: string;
}) {
  const { data } = await client.post('/api/v1/backoffice/billing/debit-credits', request, {
    headers: backofficeHeaders(),
  });
  return data;
}

export async function cancelSubscription(orgId: string) {
  const { data } = await client.post(
    `/api/v1/backoffice/organizations/${orgId}/cancel-subscription`,
    {},
    { headers: backofficeHeaders() }
  );
  return data;
}

export async function setPlan(request: {
  organizationId: string;
  planType?: string;
  planTierId?: string;
  monthlyCreditAllowance?: number;
  billingStatus?: string;
  billingPeriod?: string;
}) {
  const { data } = await client.post('/api/v1/backoffice/billing/set-plan', request, {
    headers: backofficeHeaders(),
  });
  return data;
}

export async function fetchPlans() {
  const { data } = await client.get('/api/v1/backoffice/billing/plans', {
    headers: backofficeHeaders(),
  });
  return data;
}

export async function updatePlan(planId: string, updates: Record<string, unknown>) {
  const { data } = await client.put(`/api/v1/backoffice/billing/plans/${planId}`, updates, {
    headers: backofficeHeaders(),
  });
  return data;
}

// Skills
export async function fetchOrgSkills(orgId: string) {
  const { data } = await client.get(`/api/v1/backoffice/skills/organizations/${orgId}`, {
    headers: backofficeHeaders(),
  });
  return data;
}

export async function enableSkill(
  organizationId: string,
  skillId: string,
  expiresAt?: string | null
) {
  const { data } = await client.post(
    '/api/v1/backoffice/skills/enable',
    { organizationId, skillId, expiresAt },
    { headers: backofficeHeaders() }
  );
  return data;
}

export async function disableSkill(organizationId: string, skillId: string) {
  const { data } = await client.post(
    '/api/v1/backoffice/skills/disable',
    { organizationId, skillId },
    { headers: backofficeHeaders() }
  );
  return data;
}

// User-level skills
export async function fetchUserSkills(userId: string) {
  const { data } = await client.get(`/api/v1/backoffice/skills/users/${userId}`, {
    headers: backofficeHeaders(),
  });
  return data;
}

export async function enableUserSkill(userId: string, skillId: string, expiresAt?: string | null) {
  const { data } = await client.post(
    '/api/v1/backoffice/skills/users/enable',
    { userId, skillId, expiresAt },
    { headers: backofficeHeaders() }
  );
  return data;
}

export async function disableUserSkill(userId: string, skillId: string) {
  const { data } = await client.post(
    '/api/v1/backoffice/skills/users/disable',
    { userId, skillId },
    { headers: backofficeHeaders() }
  );
  return data;
}

// Memory (Hindsight)
export async function fetchMemoryBanks(): Promise<{
  banks: Array<{
    bankId: string;
    name: string | null;
    mission: string | null;
    disposition: Record<string, number>;
    createdAt: string | null;
    updatedAt: string | null;
  }>;
  enabled: boolean;
}> {
  const { data } = await client.get('/api/v1/backoffice/memory/banks', {
    headers: backofficeHeaders(),
  });
  return data;
}

export async function fetchBankStats(bankId: string) {
  const { data } = await client.get(`/api/v1/backoffice/memory/banks/${bankId}/stats`, {
    headers: backofficeHeaders(),
  });
  return data;
}

export async function fetchMemories(
  bankId: string,
  params: { type?: string; q?: string; limit?: number; offset?: number }
) {
  const { data } = await client.get(`/api/v1/backoffice/memory/banks/${bankId}/memories`, {
    params,
    headers: backofficeHeaders(),
  });
  return data;
}

export async function deleteDocument(bankId: string, documentId: string) {
  const { data } = await client.delete(`/api/v1/backoffice/memory/banks/${bankId}/documents/${documentId}`, {
    headers: backofficeHeaders(),
  });
  return data;
}

export async function clearBankMemories(bankId: string) {
  const { data } = await client.delete(`/api/v1/backoffice/memory/banks/${bankId}/memories`, {
    headers: backofficeHeaders(),
  });
  return data;
}

export async function fetchMemoryGraph(
  bankId: string,
  params?: { type?: string; limit?: number },
) {
  const { data } = await client.get(`/api/v1/backoffice/memory/banks/${bankId}/graph`, {
    params,
    headers: backofficeHeaders(),
  });
  return data;
}

export async function reflectBank(
  bankId: string,
  query: string,
  budget: string = 'mid',
): Promise<{ text: string }> {
  const { data } = await client.post(
    `/api/v1/backoffice/memory/banks/${bankId}/reflect`,
    { query, budget },
    { headers: backofficeHeaders() },
  );
  return data;
}
